/**
 * Pure helpers behind the Goals management section: which goals belong on
 * which tab, whether a goal is past due, and which actions its overflow menu
 * should offer.
 *
 * Deliberately free of imports (React, i18n, theme) so the rules can be tested
 * on their own with `node --test` — see tests/goals/goals-view.test.ts.
 */

/** The slice of a goal these rules need. `SocialGoal` satisfies it. */
export type GoalLike = {
  done: boolean;
  archived: boolean;
  dueDate: string | null;
};

export type GoalTab = "active" | "completed" | "archived";

/**
 * Actions a goal row's overflow menu can offer. Every unfinished goal can be
 * marked complete from the menu, not only from the row's circle — `changeDue`
 * is the one that stays specific to a past-due goal, where "when was this
 * meant to happen?" is the question the user is actually being asked.
 */
export type GoalAction =
  | "complete"
  | "reopen"
  | "edit"
  | "changeDue"
  | "archive"
  | "restore"
  | "delete";

/**
 * Goal note limits, mirrored by NOTE_MAX_LENGTH / NOTE_MAX_COUNT in the API's
 * goal.service.ts. If these diverge, notes get silently truncated on save
 * after the counter told the user they fit.
 */
export const GOAL_NOTE_MAX_LENGTH = 100;
export const GOAL_NOTE_MAX_COUNT = 5;

/** Matches the `text` column's varchar(120) on social_goals. */
export const GOAL_TEXT_MAX_LENGTH = 120;

/** True once the end of the due day has passed, whatever the goal's state. */
export function isGoalDuePast(goal: GoalLike, now = Date.now()): boolean {
  if (!goal.dueDate) return false;
  const dueEnd = new Date(`${goal.dueDate}T23:59:59`).getTime();
  return Number.isNaN(dueEnd) ? false : dueEnd < now;
}

/** An open goal the user has run past the due date on. */
export function isGoalOverdue(goal: GoalLike, now = Date.now()): boolean {
  return !goal.archived && !goal.done && isGoalDuePast(goal, now);
}

/**
 * Goals for a tab. The whole history is passed in and none of these are date
 * filtered — the Week/Month selector belongs to the metrics above, and a user
 * looking at Completed or Archived wants everything they have ever done.
 */
export function goalsForTab<T extends GoalLike>(goals: T[], tab: GoalTab): T[] {
  if (tab === "archived") return goals.filter((goal) => goal.archived);
  if (tab === "completed") {
    return goals.filter((goal) => !goal.archived && goal.done);
  }
  return goals.filter((goal) => !goal.archived && !goal.done);
}

/** Goals occupying a slot against the plan's active-goal limit. */
export function activeGoals<T extends GoalLike>(goals: T[]): T[] {
  return goalsForTab(goals, "active");
}

export function goalMenuActions(goal: GoalLike, now = Date.now()): GoalAction[] {
  if (goal.archived) return ["restore", "delete"];
  if (goal.done) return ["reopen", "edit", "archive", "delete"];
  if (isGoalOverdue(goal, now)) {
    return ["complete", "changeDue", "archive", "delete"];
  }
  return ["complete", "edit", "archive", "delete"];
}

/**
 * Actions that are SHOWN but refused, and why.
 *
 * A completed goal cannot be edited: its text and due date are the record of
 * something already finished, and quietly rewriting them turns the Completed
 * tab into a list of things that were never actually done that way. Reopening
 * is the deliberate step back into an editable state — which is why Edit stays
 * on the menu, greyed, instead of disappearing: a missing row teaches nothing,
 * while a disabled one sits directly under the Reopen that enables it.
 *
 * Kept here rather than in the menu's JSX so the rule is testable on its own
 * and cannot drift from `goalMenuActions`, which decides what appears at all.
 */
export function isGoalActionDisabled(
  goal: GoalLike,
  action: GoalAction,
): boolean {
  return action === "edit" && !goal.archived && Boolean(goal.done);
}

/** Trim, drop blanks and cap, exactly as the API does before storing. */
export function sanitizeGoalNotes(notes: string[]): string[] {
  return notes
    .map((note) => note.trim().slice(0, GOAL_NOTE_MAX_LENGTH))
    .filter(Boolean)
    .slice(0, GOAL_NOTE_MAX_COUNT);
}

/** A note is one line: newlines would be stored and render as a broken gap. */
export function sanitizeGoalNoteDraft(value: string): string {
  return value.replace(/[\r\n]+/g, " ").slice(0, GOAL_NOTE_MAX_LENGTH);
}

/** What a "+ New goal" submission needs before it is worth sending. */
export function isGoalDraftValid(text: string): boolean {
  return text.trim().length > 0;
}
